import { useEffect, useState } from 'react';
import { DesktopTopBar, type DesktopTab, type ContextTrack } from '../components/desktop/TopBar';
import { TalkScreen } from '../components/desktop/TalkScreen';
import { NotesView } from '../components/NotesView';
import { CmdJOverlay } from '../components/desktop/CmdJOverlay';
import { ChatPanel } from '../components/desktop/ChatPanel';
import { NudgesModal } from '../components/desktop/NudgesModal';
import { SettingsModal } from '../components/desktop/SettingsModal';
import { useRealtimeSync } from '../hooks/useRealtimeSync';
import { useTalk } from '../hooks/useTalk';
import { sendMessage } from '../api/client';
import { PENDING_CAPTURE_KEY } from './OnboardingScreen';

const LAST_TAB_KEY = 'jotly.lastTab';

function readLastTab(): DesktopTab {
  const stored = localStorage.getItem(LAST_TAB_KEY);
  if (stored === 'talk' || stored === 'notes') return stored;
  return 'talk';
}

export function DesktopScreen() {
  useRealtimeSync();
  const [tab, setTab] = useState<DesktopTab>(readLastTab);
  const [track, setTrack] = useState<ContextTrack>('all');
  const [cmdJ, setCmdJ] = useState(false);
  const [chat, setChat] = useState(false);
  const [nudges, setNudges] = useState(false);
  const [settings, setSettings] = useState(false);

  const talk = useTalk();

  useEffect(() => {
    localStorage.setItem(LAST_TAB_KEY, tab);
  }, [tab]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(PENDING_CAPTURE_KEY);
      if (!raw) return;
      const pending: Array<{ text: string }> = JSON.parse(raw);
      localStorage.removeItem(PENDING_CAPTURE_KEY);
      for (const item of pending) {
        sendMessage(item.text).catch(() => {});
      }
    } catch { /* nothing to flush */ }
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (cmdJ) setCmdJ(false);
      else if (nudges) setNudges(false);
      else if (settings) setSettings(false);
      else if (chat) setChat(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cmdJ, chat, nudges, settings]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      const k = e.key.toLowerCase();
      if (k === 'j') {
        e.preventDefault();
        setCmdJ((o) => !o);
      } else if (e.key === '/' || e.key === '?') {
        e.preventDefault();
        setChat((o) => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="jd-shell">
      <DesktopTopBar
        tab={tab}
        onTab={setTab}
        track={track}
        onTrack={setTrack}
        onOpenNudges={() => setNudges(true)}
        onOpenSettings={() => setSettings(true)}
      />

      <main className="jd-main">
        {tab === 'talk' && <TalkScreen talk={talk} track={track} />}
        {tab === 'notes' && <NotesView />}

        <CmdJOverlay
          open={cmdJ}
          onClose={() => setCmdJ(false)}
          onAsk={(q) => {
            setTab('talk');
            talk.ask(q);
          }}
        />
        <ChatPanel open={chat} onClose={() => setChat(false)} />
      </main>

      {nudges && <NudgesModal onClose={() => setNudges(false)} />}
      {settings && (
        <SettingsModal
          onClose={() => setSettings(false)}
          onOpenNudges={() => { setSettings(false); setNudges(true); }}
        />
      )}
    </div>
  );
}
